import React, { useState, useRef, useEffect, useCallback } from "react";
import { Container, Row, Col, Form, Button, Card, Spinner } from "react-bootstrap";
import "../registerDriverPage/RegisterDriverPage.css";
import "./OTPVerificationPage.css";
import Footer from "../footer/Footer";
import { useNavigate } from "react-router-dom";
import { authAPI } from "../../api/apiSystem";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

const OTP_LENGTH = 6;

export default function OTPVerificationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const inputRefs = useRef([]);
  const isSubmittingRef = useRef(false);

  // ── Countdown timer للـ resend ───────────────────────────────
  useEffect(() => {
    if (resendTimer <= 0) return;
    const id = setInterval(() => {
      setResendTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [resendTimer]);

  // ── Alerts ────────────────────────────────────────────────────
  const showSuccessAlert = (message) => {
    Swal.fire({
      icon: "success",
      title: t("otpVerification.success.title"),
      text: message,
      confirmButtonColor: "#fd7e14",
      confirmButtonText: t("otpVerification.success.confirmButton"),
      timer: 2000,
      timerProgressBar: true,
    });
  };

  const showErrorAlert = (message) => {
    Swal.fire({
      icon: "error",
      title: t("otpVerification.error.title"),
      text: message,
      confirmButtonColor: "#dc3545",
      confirmButtonText: t("otpVerification.error.confirmButton"),
    });
  };

  const showLoadingAlert = () => {
    Swal.fire({
      title: t("otpVerification.loading.title"),
      text: t("otpVerification.loading.text"),
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
  };

  // ── Verify OTP ────────────────────────────────────────────────
  const verifyOtp = useCallback(
    async (code) => {
      // منع استدعاء مكرر
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;

      setLoading(true);
      setError("");
      showLoadingAlert();

      try {
        console.log("Submitting OTP:", code);
        const response = await authAPI.verifyOtp({ otp: code });

        if (response.success) {
          setSuccess(true);
          const innerData = response.data.data || {};
          const { access_token } = innerData;

          if (access_token) {
            localStorage.setItem("token", access_token);
          }

          const loggedUser =
            innerData.driver ||
            innerData.customer ||
            innerData.user ||
            null;

          if (loggedUser) {
            localStorage.setItem("user", JSON.stringify(loggedUser));

            let type = null;
            if (loggedUser.role_name) {
              const role = loggedUser.role_name.toLowerCase();
              if (role === "driver") type = "driver";
              else if (role === "user" || role === "customer") type = "client";
            }

            Swal.close();
            showSuccessAlert(
              t("otpVerification.success.verificationSuccessful"),
            );

            setTimeout(() => {
              if (type === "driver") {
                localStorage.setItem("userType", "driver");
                navigate("/driver/driver-details");
              } else if (type === "client") {
                localStorage.setItem("userType", "client");
                navigate("/");
              } else {
                navigate("/");
              }
            }, 1500);
          } else {
            Swal.close();
            navigate("/");
          }
        } else {
          Swal.close();
          const msg =
            response.message || t("otpVerification.error.invalidCode");
          setError(msg);
          showErrorAlert(msg);

          // 🔄 اهتزاز + تفريغ الحقول بعد الخطأ
          setOtp(Array(OTP_LENGTH).fill(""));
          setTimeout(() => inputRefs.current[0]?.focus(), 300);
        }
      } catch (err) {
        console.error("OTP verification error:", err);
        Swal.close();
        const msg = t("otpVerification.error.generic");
        setError(msg);
        showErrorAlert(msg);

        setOtp(Array(OTP_LENGTH).fill(""));
        setTimeout(() => inputRefs.current[0]?.focus(), 300);
      } finally {
        setLoading(false);
        isSubmittingRef.current = false;
      }
    },
    [navigate, t],
  );

  // ── Handle input change ──────────────────────────────────────
  const handleChange = (value, index) => {
    // اقبل رقم واحد فقط
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError("");

    // انتقل للحقل التالي عند الكتابة
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // ✅ التحقق التلقائي عند اكتمال كل الأرقام
    const code = newOtp.join("");
    if (code.length === OTP_LENGTH && !newOtp.includes("")) {
      // استدعاء بعد tick قصير عشان الـ state تتحدث
      setTimeout(() => verifyOtp(code), 150);
    }
  };

  // ── Handle keyboard navigation ────────────────────────────────
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();

      const newOtp = [...otp];

      if (newOtp[index]) {
        // لو الحقل فيه رقم → امسحه
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        // لو فاضي → ارجع للخلف وامسح
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // ── Handle paste ─────────────────────────────────────────────
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;

    const digits = pasted.slice(0, OTP_LENGTH).split("");
    const newOtp = Array(OTP_LENGTH).fill("");
    digits.forEach((d, i) => {
      newOtp[i] = d;
    });
    setOtp(newOtp);
    setError("");

    // ✅ لو الكود كامل → تحقق تلقائي
    if (newOtp.join("").length === OTP_LENGTH) {
      setTimeout(() => verifyOtp(newOtp.join("")), 150);
    } else {
      // focus على أول حقل فاضي
      const nextIndex = digits.length;
      inputRefs.current[nextIndex]?.focus();
    }
  };

  // ── Resend OTP ────────────────────────────────────────────────
  const handleResendOTP = async () => {
    if (resendTimer > 0) return;

    setResendLoading(true);

    try {
      const email = localStorage.getItem("confirm_email");

      if (!email) {
        showErrorAlert(t("otpVerification.error.noEmailFound"));
        return;
      }

      const res = await authAPI.confirmAccount({ email });

      if (res.success) {
        showSuccessAlert(t("otpVerification.success.otpResent"));
        // ابدأ countdown 60 ثانية
        setResendTimer(60);

        // نظف الحقول
        setOtp(Array(OTP_LENGTH).fill(""));
        setError("");
        inputRefs.current[0]?.focus();
      } else {
        showErrorAlert(
          res.message || t("otpVerification.error.resendFailed"),
        );
      }
    } catch (err) {
      console.error("Resend OTP error:", err);
      showErrorAlert(t("otpVerification.error.generic"));
    } finally {
      setResendLoading(false);
    }
  };

  // ── Format timer ──────────────────────────────────────────────
  const formatTimer = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <>
      <div className="register-driver-section otp-verification-section">
        <Container>
          <Row className="justify-content-center align-items-center h-100">
            <Col md={8} lg={6}>
              <Card
                className={`shadow-lg p-4 border-0 register-driver-card ${
                  success ? "otp-success-card" : ""
                }`}
              >
                <Card.Body>
                  <h4 className="text-center mb-4 text-dark fw-bold">
                    {t("otpVerification.title")}
                  </h4>

                  <p className="text-center text-muted mb-4">
                    {t("otpVerification.description")}
                  </p>

                  {error && (
                    <p className="text-danger text-center fw-semibold otp-error-text">
                      {error}
                    </p>
                  )}

                  <Form onSubmit={(e) => e.preventDefault()}>
                    <div className="otp-input-row" onPaste={handlePaste}>
                      {otp.map((digit, idx) => (
                        <input
                          key={idx}
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleChange(e.target.value, idx)}
                          onKeyDown={(e) => handleKeyDown(e, idx)}
                          onFocus={(e) => e.target.select()}
                          className={`otp-input-box ${
                            loading ? "otp-loading" : ""
                          } ${success ? "otp-success" : ""} ${
                            error ? "otp-error" : ""
                          }`}
                          ref={(el) => (inputRefs.current[idx] = el)}
                          disabled={loading || success}
                        />
                      ))}
                    </div>

                    {/* ✅ مؤشر التحقق التلقائي */}
                    {loading && (
                      <div className="otp-verifying d-flex align-items-center justify-content-center mt-3">
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                          style={{ color: "#fd7e14" }}
                        />
                        <span className="text-muted small">
                          {t(
                            "otpVerification.verifying",
                            "Wird überprüft...",
                          )}
                        </span>
                      </div>
                    )}

                    <Col xs={12} className="mt-4">
                      <Button
                        type="submit"
                        variant="warning"
                        className="w-100 bg-orange-500 text-white"
                        disabled={loading || otp.includes("") || success}
                        onClick={() => verifyOtp(otp.join(""))}
                      >
                        {loading
                          ? t("otpVerification.submitButtonLoading")
                          : t("otpVerification.submitButton")}
                      </Button>
                    </Col>

                    <Col xs={12} className="mt-3">
                      <div className="text-center">
                        <Button
                          type="button"
                          variant="outline-secondary"
                          className="w-100"
                          onClick={handleResendOTP}
                          disabled={resendLoading || resendTimer > 0}
                        >
                          {resendLoading
                            ? t("otpVerification.resendButtonLoading")
                            : resendTimer > 0
                            ? `${t(
                                "otpVerification.resendButtonCountdown",
                                "Erneut senden in",
                              )} ${formatTimer(resendTimer)}`
                            : t("otpVerification.resendButton")}
                        </Button>
                      </div>
                    </Col>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Footer />
    </>
  );
}